import ApiService from '@/services/apiService';
import { cloneDeep, get } from 'lodash';
import { mapState } from 'vuex';
export default {
  name: 'CoreTenantDialogDeleteTenant',
  props: {
    open: {
      type: Boolean,
      required: false,
      default: false
    },
    accountId: {
      type: Number,
      required: true,
      default: -1
    }
  },
  data: function () {
    return {
      accountInformation: null,
      subAccountTree: null,
      digitalTwinsTree: null,
      deviceDriversTree: null,
      deviceTemplatesTree: null,
      accountInformationFetched: false,
      accountInformationError: false,
      confirmationInput: null,
      accountVirtualDeviceIds: [],
      deleteVirtualDevicesInRemoteSystem: false,
      containsDeviceDriverUsedByOtherAccount: false,
      hasSubAccounts: false
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'color']),
    deleteBtnDisabled() {
      if (!this.accountInformationFetched) {
        return true;
      }
      if (this.accountVirtualDeviceIds.length > 0) {
        return parseInt(this.confirmationInput) !== this.accountVirtualDeviceIds.length;
      }
      if (this.containsDeviceDriverUsedByOtherAccount || this.hasSubAccounts) {
        return true;
      }
      return parseInt(this.confirmationInput) !== this.accountId;
    },
    deleteButtonText() {
      if (this.accountVirtualDeviceIds.length) {
        return this.$i18n_t('accMgmt.tenant.deleteVirtualDevices');
      }
      return this.$i18n_t('Common.delete');
    },
    shouldLoadData() {
      return this.open && this.accountId > 0;
    }
  },
  watch: {
    shouldLoadData: {
      handler() {
        if (this.shouldLoadData) {
          this.getAccountDetails();
        }
        this.confirmationInput = null;
      },
      immediate: true
    }
  },
  methods: {
    getAccountDetails() {
      this.accountInformationFetched = false;
      this.accountInformationError = false;
      this.getTenantRelatedData().then(result => {
        this.accountInformation = result;
        this.subAccountTree = this.buildSubAccountTree(result);
        this.digitalTwinsTree = this.buildDigitalTwinsTree(result);
        this.deviceDriversTree = this.buildDeviceDriversTree(result);
        this.deviceTemplatesTree = this.buildDeviceTemplatesTree(result);
        this.accountVirtualDeviceIds = result.virtualDevices;
        this.accountInformationFetched = true;
      }).catch(() => {
        // getTenantById() responds on errors with wrong messages
        // not matte which request fails we only recognize it as generic for now
        // https://digimondo.atlassian.net/browse/TWIN-617
        this.accountInformationError = true;
      });
    },
    async getTenantRelatedData() {
      const account = await ApiService.tenantGetById(this.accountId, ['descendents']).then(accountData => {
        return {
          name: accountData.name,
          id: accountData.id,
          parentId: accountData.parentId,
          scopeId: accountData.scopeId,
          children: this.buildListFromNestedChildren(accountData)
        };
      });
      const digitalTwinsResponse = await ApiService.digitalTwinsFetch(this, null, [account.scopeId], null, ['bare']);
      const digitalTwins = digitalTwinsResponse.digitaltwins;
      const deviceDrivers = await ApiService.virtualDeviceDrivers.fetch({
        fields: 'id,title',
        sortBy: 'title',
        accountId: account.id,
        limit: 500,
        usedByAnotherAccount: true,
        limitToAccountOnly: true
      });
      this.containsDeviceDriverUsedByOtherAccount = get(deviceDrivers, 'totalCount') > 0;
      this.hasSubAccounts = (account.children || []).length;
      const deviceTemplates = await ApiService.deviceTemplatesFetch({
        tenantIds: [account.id]
      });
      const virtualDevices = await ApiService.virtualDeviceFetch(this,
      // context
      null,
      // userId
      [],
      // scopeIds
      [{
        type: 'tenantIds',
        ids: [this.accountId]
      }],
      //filters
      null,
      // options
      null,
      // orderBy
      0,
      // offset
      500,
      // limit
      null,
      //searchItems
      null,
      //restricted_query
      null,
      // includeIdentifiers
      null,
      // includeProperties
      null,
      // includeDrivers
      null,
      // searchOperator
      {},
      // cancelOptions
      true // ids_only
      );
      return {
        account: {
          ...account,
          children: undefined
        },
        subAccounts: account.children,
        digitalTwins,
        deviceDrivers: deviceDrivers.deviceDrivers,
        deviceTemplates: deviceTemplates.data,
        virtualDevices: virtualDevices.virtualDevices
      };
    },
    buildSubAccountTree(data) {
      // Accounts need the root node to be able to build the tree up to root node parentId
      const withRootNode = [data.account, ...data.subAccounts];
      const rootNodeParentId = data.account.parentId;
      const children = this.buildTreeFromList(withRootNode, rootNodeParentId)[0].children || [];
      const name = this.$i18n_tc('accMgmt.tenant.deleteConfimationSubAccounts', data.subAccounts.length, {
        n: data.subAccounts.length
      });
      return [{
        id: null,
        parentId: null,
        name,
        children
      }];
    },
    buildDigitalTwinsTree(data) {
      const children = this.buildTreeFromList(data.digitalTwins);
      const title = this.$i18n_tc('accMgmt.tenant.deleteConfimationDigitalTwins', data.digitalTwins.length, {
        n: data.digitalTwins.length
      });
      return [{
        id: null,
        parentId: null,
        title,
        children
      }];
    },
    buildDeviceDriversTree(data) {
      const children = data.deviceDrivers;
      const title = this.$i18n_tc('accMgmt.tenant.deleteConfimationDeviceDrivers', data.deviceDrivers.length, {
        n: data.deviceDrivers.length
      });
      return [{
        id: null,
        parentId: null,
        title,
        children
      }];
    },
    buildDeviceTemplatesTree(data) {
      const children = data.deviceTemplates;
      const title = this.$i18n_tc('accMgmt.tenant.deleteConfimationDeviceTemplates', data.deviceTemplates.length, {
        n: data.deviceTemplates.length
      });
      return [{
        id: null,
        parentId: null,
        title,
        children
      }];
    },
    handleDeleteAction() {
      if (this.accountVirtualDeviceIds.length) {
        this.deleteVirtualDevices();
      } else {
        this.$emit('delete', this.accountId);
      }
    },
    deleteVirtualDevices() {
      const bulkDeleteData = {
        entities: this.accountVirtualDeviceIds,
        title: `Preparation for account deletion: Delete all virtual devices on account ID: ${this.accountId}`,
        tenantId: this.accountId,
        executions: {
          deletionOfDevices: {
            deleteFromRemote: this.deleteVirtualDevicesInRemoteSystem
          }
        }
      };
      ApiService.createBackgroundRunnerBulkEdit(bulkDeleteData).then(() => {
        this.$emit('cancel');
        this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.bulkEditSuccess', {
          n: this.accountVirtualDeviceIds.length
        }), 'success');
      }).catch(error => {
        this.$emit('cancel');
        this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
      });
    },
    buildListFromNestedChildren(object = {}) {
      if (!Array.isArray(object.children)) {
        return [];
      }
      return object.children.reduce((prev, cur) => {
        return [...prev, ...[{
          id: cur.id,
          parentId: cur.parentId,
          name: cur.name
        }], ...(cur.children ? this.buildListFromNestedChildren(cur) : [])];
      }, []);
    },
    buildTreeFromList(array, fromId = null) {
      if (!Array.isArray(array)) {
        return [];
      }
      const data = cloneDeep(array);

      // create id mapping to index array
      const idMapping = data.reduce((prev, cur, idx) => {
        return {
          ...prev,
          [cur.id]: idx
        };
      }, {});
      const root = [];
      data.forEach(el => {
        // get root element based on given parameters
        if (el.id === fromId && fromId !== fromId || el.parentId === fromId) {
          root.push(el);
          return;
        }

        // get parent element via id mapping array
        const parentEl = data[idMapping[el.parentId]];

        // add to parents `children` array
        if (parentEl) {
          parentEl['children'] = [...(parentEl['children'] || []), el];
          // as the original request already contains children (with just an ID), filter elements that do not have a name or title
          parentEl['children'] = parentEl['children'].filter(child => child.name || child.title);
        }
      });
      if (root.length === 1 && !root[0].children) {
        root[0]['children'] = [];
      }
      return root || [];
    }
  }
};